/**
 * Safe external navigation helpers (open-redirect / tabnabbing defenses).
 * - HTTPS only (http allowed for localhost in non-production)
 * - Host allowlist (exact or subdomain suffix)
 * - Blocks javascript:, data:, vbscript:, protocol-relative URLs
 */

const DANGEROUS_SCHEME = /^(javascript|data|vbscript|file|blob):/i;

function hostFromUrlString(value) {
  if (!value || typeof value !== "string") {
    return null;
  }
  try {
    const trimmed = value.trim();
    if (!trimmed || DANGEROUS_SCHEME.test(trimmed) || trimmed.startsWith("//")) {
      return null;
    }
    const parsed = new URL(trimmed);
    return parsed.hostname ? parsed.hostname.toLowerCase() : null;
  } catch {
    return null;
  }
}

/**
 * Normalize an allowlist entry to a hostname.
 * Hostnames are used as-is; URL-shaped values (scheme or path) are parsed.
 */
function hostFromAllowlistEntry(value) {
  const trimmed = value.trim().toLowerCase().replace(/^\./, "");
  if (!trimmed) {
    return null;
  }
  if (trimmed.includes("://") || trimmed.includes("/")) {
    return hostFromUrlString(trimmed);
  }
  return trimmed;
}

/**
 * Build allowlist from env: explicit hosts + hosts derived from known bank link envs.
 */
export function getAllowedExternalRedirectHosts() {
  const hosts = new Set();

  const explicit = process.env.ALLOWED_EXTERNAL_REDIRECT_HOSTS || "";
  explicit.split(",").forEach((entry) => {
    const host = hostFromAllowlistEntry(entry);
    if (host) {
      hosts.add(host);
    }
  });

  const derivedFrom = [
    process.env.OPEN_LOLC_ACC_LINK,
    process.env.APPLY_CREDIT_CARD_LINK,
    process.env.FAQ_LINK,
    process.env.RATE_AND_CHARGES_LINK,
    process.env.PRODUCT_INFO_LINK,
    process.env.NFS_TERMS_CONDITION_LINK,
    process.env.NFS_FD_TERMS_CONDITION_LINK,
    process.env.MAIN_CDN_URL,
    process.env.NFS_PUBLIC_IMAGE_URL,
  ];

  derivedFrom.forEach((url) => {
    const host = hostFromUrlString(url);
    if (host) {
      hosts.add(host);
    }
  });

  return Array.from(hosts);
}

function isHostAllowed(hostname, allowlist) {
  const h = hostname.toLowerCase();
  return allowlist.some((allowed) => {
    const a = allowed.toLowerCase().replace(/^\./, "");
    return h === a || h.endsWith(`.${a}`);
  });
}

/**
 * Returns a normalized absolute HTTPS URL string if safe, otherwise null.
 */
export function getSafeExternalUrl(
  rawUrl,
  allowlist = getAllowedExternalRedirectHosts(),
) {
  if (!rawUrl || typeof rawUrl !== "string") {
    return null;
  }

  const trimmed = rawUrl.trim();
  if (!trimmed) {
    return null;
  }

  // Protocol-relative and dangerous schemes
  if (trimmed.startsWith("//") || DANGEROUS_SCHEME.test(trimmed)) {
    return null;
  }

  let parsed;
  try {
    parsed = new URL(trimmed);
  } catch {
    return null;
  }

  // Reject embedded credentials
  if (parsed.username || parsed.password) {
    return null;
  }

  const isLocalHttp =
    parsed.protocol === "http:" &&
    (parsed.hostname === "localhost" ||
      parsed.hostname === "127.0.0.1" ||
      parsed.hostname === "[::1]") &&
    process.env.NODE_ENV !== "production";

  if (parsed.protocol !== "https:" && !isLocalHttp) {
    return null;
  }

  if (!allowlist.length || !isHostAllowed(parsed.hostname, allowlist)) {
    return null;
  }

  return parsed.toString();
}

/**
 * Opens an allowlisted external URL in a new tab with noopener,noreferrer.
 * @returns {boolean} true if the URL was allowed (open or same-tab fallback attempted)
 */
export function openSafeExternalUrl(
  rawUrl,
  target = "_blank",
  { fallbackSameTab = false } = {},
) {
  const safeUrl = getSafeExternalUrl(rawUrl);
  if (!safeUrl) {
    return false;
  }

  const win = window.open(safeUrl, target, "noopener,noreferrer");
  if (win) {
    try {
      win.opener = null;
    } catch {
      // ignore cross-origin / browser restrictions
    }
    return true;
  }

  if (fallbackSameTab) {
    window.location.assign(safeUrl);
    return true;
  }

  return false;
}
